<script setup lang="ts">
import { useNetwork } from '@vueuse/core';
import { computed, reactive, ref } from 'vue';
import { useClassStore, useLangStore, useQuizStore, useSessionStore, useUserStore } from '../../stores';
import { Session, VocabResult } from '../../types/session';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'
import { QuizState } from '../../types/quiz';
import Button from './Button.vue';
import { useSettingStore } from '../../stores/setting';
import Input from '../Form/Input.vue';
import { UserRole } from '../../types/user';
import { toBase64 } from '../../utils/utils';

const session = useSessionStore()
const classStore = useClassStore()
const user = useUserStore()
const quizStore = useQuizStore()
const lang = useLangStore()
const setting = useSettingStore()
const network = reactive(useNetwork())

const vocabRecordAttempts = computed<VocabResult[]>(() => session.vocabRecordAttempts?.[classStore.roomName]?.[user.username || '']?.[session.currentVocabId]?.[session.vocabRecordingType] || [])

const testVersion = ref<string | null>(null)

const handleReload = () => {
    window.location.reload()
}

function showTranslationKeys() {
    lang.i18n.global.setLocaleMessage(lang.locale, {})
}
</script>

<template>
    <div class="flex flex-col w-full h-full overflow-hidden gap-3">
        <TabGroup as="div" class="flex gap-2 flex-col w-full flex-1 overflow-hidden">
            <TabList aria-selected="div"
                class="flex space-x-1 rounded-xl bg-gradient-to-r from-sky-400 to-blue-600 p-1">
                <Tab as="template" v-slot="{ selected }">
                    <button
                        class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 "
                        :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                        Network data
                    </button>
                </Tab>
                <Tab as="template" v-slot="{ selected }">
                    <button
                        class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 "
                        :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                        Class content
                    </button>
                </Tab>
                <Tab as="template" v-slot="{ selected }">
                    <button
                        class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 "
                        :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                        Vocab data
                    </button>
                </Tab>
                <Tab as="template" v-slot="{ selected }">
                    <button
                        class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 "
                        :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                        Practice data
                    </button>
                </Tab>
                <Tab as="template" v-slot="{ selected }">
                    <button
                        class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 "
                        :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                        Quiz data
                    </button>
                </Tab>
                <Tab as="template" v-slot="{ selected }">
                    <button
                        class="transistion duration-300 w-full rounded-lg py-2.5 font-bold leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2 "
                        :class="{
                            'bg-white !text-blue-500 hover:bg-white !hover:text-blue-500': selected,
                            'text-blue-100 hover:bg-white/[0.12] hover:text-white': !selected
                        }">
                        Setting
                    </button>
                </Tab>

            </TabList>
            <TabPanels as="div" class="w-full flex-1 bg-neutral-100 p-5 rounded-xl  overflow-auto relative">
                <TabPanel>
                    <Button class="w-full mb-3" @click="showTranslationKeys">
                        Show translation keys
                    </Button>
                    <pre>{{ JSON.stringify(network, null, 2) }}</pre>
                </TabPanel>
                <TabPanel>
                    <pre class="whitespace-pre-wrap break-all">{{ toBase64(session.classContent) }}</pre>
                </TabPanel>
                <TabPanel>
                    <pre class="whitespace-pre-wrap break-all">{{ toBase64(vocabRecordAttempts) }}</pre>
                </TabPanel>
                <TabPanel>
                    <pre class="whitespace-pre-wrap break-all">{{ toBase64(session.practiceUrl) }}</pre>
                    <pre class="whitespace-pre-wrap break-all">{{ toBase64(session.currentPracticeResult) }}</pre>
                </TabPanel>
                <TabPanel>
                    <Button v-if="user.userRole === UserRole.teacher" class="w-full mb-3" @click="quizStore.updateQuizState(QuizState.NOT_STARTED)">
                        Reset quiz
                    </Button>
                    <pre class="whitespace-pre-wrap break-all">{{ toBase64(session.classContent?.[Session.quiz]) }}</pre>
                </TabPanel>
                <TabPanel>
                    <Input v-model="testVersion" />
                    <Button class="w-full mb-3" @click="setting.serverVersion = testVersion">
                        Set version
                    </Button>
                </TabPanel>
            </TabPanels>
        </TabGroup>
        <div class="flex w-full justify-center gap-5">
            <Button @click="handleReload">Reload</Button>
        </div>
    </div>
</template>